import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useState,
  useCallback,
  useEffect,
} from 'react'
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { Avatar, Input, Modal } from 'antd'
import clsx from 'clsx'
import styles from './Create.module.css'
import AvatarEditorItem, {
  AvatarEditorItemOutput,
} from '@/app/[lang]/@desktop/_components/AvatarEditorItem'
import Image from 'next/image'
import { useRecoilState } from 'recoil'
import { createStateFactory } from 'Store/create'
import {
  UploadCharImg,
  UploadCharImgOutput,
} from '@/app/[lang]/@desktop/(app)/create/_components/UploadCharImg'
import LoadingImg from '../../../../../icons/LoadingImg'
import {
  MAX_IMAGE_PROMPT_LENGTH,
  UPLOAD_IMG_CROP_RATIO,
} from '@/constant/limit'
import TipsyButton from 'Comps/ui-kit/TipsyButton'
import { CreateRulesIcon, CreateSelectIcon, CreateSelectLargeIcon } from 'Icons'
import { usePathname, useSearchParams } from 'next/navigation'
import useCreateImg from 'Hooks/create/useCreateImg'
import './creat.css'
import { transformImage, transformImageAuto } from 'Utils/img'
import LoadingProgress from 'Comps/common/LoadingProgress'
import { CreateUploadImgBorder } from 'Comps/svg/CreateUploadImgBorder'
import { GenerateUploadCharImg } from './_components/GenerateUploadImg'
import UploadGif from './_components/UploadGif'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'
import { ImageStyle } from '@/types/create'
import { useMessage } from '@/provider/MessageProvider'

type CreateImgProps = {
  setStep?: Dispatch<SetStateAction<number>>
  scene?: string
  hideNext?: boolean
  onConfirm?: () => void
  containerHeight?: string
  modalSurface?: boolean
}

export default function CreateImg({
  setStep,
  scene = 'character_image',
  hideNext = false,
  onConfirm,
  containerHeight,
  modalSurface = false,
}: CreateImgProps) {
  const { t, lang } = useI18n()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isUpdateChar =
    pathname === withLocale('/update', lang) ||
    searchParams.get('update') === 'true'
  const [createCharForm, setCreateCharForm] = useRecoilState(
    createStateFactory(false, scene)
  )
  const [updateCharForm, setUpdateCharForm] = useRecoilState(
    createStateFactory(true, scene)
  )

  const charForm = isUpdateChar ? updateCharForm : createCharForm
  const setCharForm = isUpdateChar ? setUpdateCharForm : setCreateCharForm
  const {
    createImgStep,
    imgPrompt,
    imgStyleKey,
    uploadImgType,
    uploadUploadImageUrl,
    uploadUpscaleImageUrl,
    uploadVaryImageUrl,
    charImageUrl,
    charFaceImagePreviewUrl,
    charFaceImageCroppedArea,
    charFaceImageCroppedAreaPixels,
    croppedArea,
    uploadImgUploading,
    generatedNum,
  } = charForm
  const [openPcImgGallery, setOpenPcImgGallery] = useState(false)
  const [currPcImgGalleryIndex, setCurrPcImgGalleryIndex] = useState<number>(-1)
  const [editFaceOpen, setEditFaceOpen] = useState(false)
  const [moderationFailedImages, setModerationFailedImages] = useState<
    Set<number>
  >(new Set())
  const messageApi = useMessage()
  const {
    isPreviewStep,
    selectedPcImgIndex,
    croppingCharFaceImage,
    genPromptLoading,
    openImgGallery,
    currImgGalleryIndex,
    generatedLoading,
    generatedImages,
    selectedImgIndex,
    generatedPcImages,
    generateImgReferencePreviewUrl,
    uploadImgReferencePreviewUrl,
    isImgPromptError,
    imgStyleData,
    setCreateImgStep,
    setIsPreviewStep,
    setOpenImgGallery,
    setCurrImgGalleryIndex,
    setCroppedArea,
    setCroppedAreaPixels,
    onImgPromptChange,
    handleGenerateImg,
    handleStopGeneratingImage,
    handleUploadImg,
    handleImgChecked,
    handleSaveFaceImg,
    handleGoPreviewStep,
    openImgGalleryWithIndex,
    goNextStep,
    checkCharImageByIndex,
    checkCharPcImageByIndex,
  } = useCreateImg({
    setStep: setStep,
    setEditFaceOpen: setEditFaceOpen,
    scene: scene,
  })

  useEffect(() => {
    if (generatedImages && generatedImages.length > 0) {
      const hasFailedImages = generatedImages.some(
        (item) => item && item.moderation_status === true
      )

      if (hasFailedImages && !moderationFailedImages.has(-1)) {
        messageApi.warning({
          key: 'moderation-failed-batch',
          content: t('Image failed moderation review. You were not charged.'),
          duration: 3,
        })
        setModerationFailedImages((prev) => new Set(prev).add(-1))
      }
    }
  }, [generatedImages, moderationFailedImages, messageApi, t])

  const handleClickRight = (
    e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    e.preventDefault()
    e.stopPropagation()
    const index = currImgGalleryIndex
    if (index < generatedNum - 1) {
      setCurrImgGalleryIndex(index + 1)
    }
  }
  const handleClickLeft = (
    e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    e.preventDefault()
    e.stopPropagation()
    const index = currImgGalleryIndex
    if (index > 0) {
      setCurrImgGalleryIndex(index - 1)
    }
  }
  const handlePcImgGalleryClickRight = () => {
    const index = currPcImgGalleryIndex
    if (index < 1) {
      setCurrPcImgGalleryIndex(index + 1)
    }
  }
  const handlePcImgGalleryClickLeft = () => {
    const index = currPcImgGalleryIndex
    if (index > 0) {
      setCurrPcImgGalleryIndex(index - 1)
    }
  }
  const hasGeneratedImages = generatedImages?.some((item) => !!item)
  const setImgStyleKey = useCallback(
    (key: number | null) => {
      setCharForm((prev) => ({
        ...prev,
        imgStyleKey: key,
      }))
    },
    [setCharForm]
  )

  return (
    <>
      <div
        className={clsx(
          'flex w-full flex-row text-white',
          modalSurface
            ? 'overflow-hidden rounded-2xl border border-white/10 bg-[rgba(11,2,15,0.88)] shadow-[0_24px_80px_rgba(0,0,0,0.55)] backdrop-blur-[14px]'
            : 'rounded-[10px] bg-black/10 shadow-[0_2px_4px_0_rgba(255,255,255,0.05)]'
        )}
        style={{
          height: containerHeight || 'calc(100vh - 140px)',
        }}
      >
        <div
          className={clsx(
            'relative w-[70%] rounded-es-[10px] rounded-ss-[10px] border-b-[0.5px] border-l-[0.5px] border-t-[0.5px] border-white/20',
            modalSurface && 'border-none'
          )}
        >
          <div
            className={clsx(
              'transition-scrollbar overflow-y-auto [scrollbar-gutter:stable]',
              modalSurface ? 'px-6 pb-28' : 'pl-5 pr-2'
            )}
            style={{ maxHeight: 'calc(100% - 82px)' }}
          >
            {!isPreviewStep && (
              <div
                className={clsx(
                  'relative flex h-fit gap-3.5',
                  modalSurface && 'gap-4'
                )}
              >
                <div className="flex w-[33%] flex-col pt-4">
                  <div
                    className={clsx(
                      'flex cursor-pointer items-center justify-center text-[15px] tracking-[0.4px]',
                      modalSurface &&
                        'rounded-t-xl border border-white/10 border-b-transparent bg-white/[0.03] p-1 text-sm'
                    )}
                  >
                    <div
                      className={clsx(
                        'flex h-full flex-1 items-center justify-center rounded-t-lg px-1 py-1.5 text-center',
                        createImgStep === 'uploadImg' && 'bg-white/5',
                        modalSurface &&
                          'rounded-lg py-2 text-white/55 transition-colors',
                        modalSurface &&
                          createImgStep === 'uploadImg' &&
                          'bg-[#9C4844] text-white'
                      )}
                      onClick={() =>
                        !generatedLoading && setCreateImgStep('uploadImg')
                      }
                    >
                      {t('Upload Image')}
                    </div>
                    <div
                      className={clsx(
                        'h-full flex-1 rounded-t-lg px-1 py-1.5 text-center',
                        createImgStep === 'generateImg' && 'bg-white/5',
                        modalSurface &&
                          'rounded-lg py-2 text-white/55 transition-colors',
                        modalSurface &&
                          createImgStep === 'generateImg' &&
                          'bg-[#9C4844] text-white'
                      )}
                      onClick={() =>
                        !generatedLoading && setCreateImgStep('generateImg')
                      }
                    >
                      {t('Generate Image')}
                    </div>
                  </div>

                  <div
                    className={clsx(
                      'flex-1 rounded-b-lg bg-white/5 p-5',
                      createImgStep === 'uploadImg' && 'rounded-tr-lg',
                      createImgStep === 'generateImg' && 'rounded-tl-lg',
                      modalSurface &&
                        'rounded-xl border border-dashed border-white/20 bg-white/[0.04] p-4'
                    )}
                  >
                    {createImgStep === 'uploadImg' && (
                      <div
                        className={clsx(
                          'relative aspect-square w-full border border-transparent',
                          {
                            'rounded-lg !border-white/20':
                              uploadImgReferencePreviewUrl?.length,
                          }
                        )}
                      >
                        {!uploadImgReferencePreviewUrl && (
                          <CreateUploadImgBorder className="absolute" />
                        )}
                        <UploadCharImg
                          disabled={generatedLoading}
                          setCroppedArea={setCroppedArea}
                          setCroppedAreaPixels={setCroppedAreaPixels}
                          scene={scene}
                        />
                      </div>
                    )}
                    {createImgStep === 'generateImg' && (
                      <div
                        className={clsx(
                          'relative aspect-square w-full border border-transparent',
                          {
                            'rounded-lg !border-white/20':
                              generateImgReferencePreviewUrl?.length,
                          }
                        )}
                      >
                        {!generateImgReferencePreviewUrl && (
                          <CreateUploadImgBorder className="absolute" />
                        )}
                        <GenerateUploadCharImg
                          disabled={generatedLoading || genPromptLoading}
                          scene={scene}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={clsx(
                    'flex w-[67%] flex-col pt-5',
                    modalSurface &&
                      createImgStep === 'generateImg' &&
                      'rounded-xl border border-white/10 bg-white/[0.03] p-4'
                  )}
                >
                  {createImgStep === 'uploadImg' && (
                    <UploadGif disabled={false} scene={scene} />
                  )}
                  {createImgStep === 'generateImg' && (
                    <>
                      <div className="flex items-center justify-between pb-2 tracking-[0.4px]">
                        <span className="text-sm text-white/60">
                          {t('Image Description')} *
                        </span>
                        {genPromptLoading && (
                          <div className="flex h-5 items-center">
                            <LoadingImg className="scale-[.6]" />
                          </div>
                        )}
                      </div>

                      <div className="flex w-full flex-1 flex-col gap-3.5">
                        <div
                          className={clsx(
                            'flex-1 rounded-lg py-2',
                            createImgStep === 'generateImg' && 'bg-white/5',
                            modalSurface && 'bg-white/10 p-0'
                          )}
                        >
                          <Input.TextArea
                            className={clsx(
                              'placeholder:text-gray-756A6E !h-full !min-h-[80px] grow overflow-y-hidden rounded-lg bg-white/5 px-3 py-2 text-sm [scrollbar-gutter:stable_both-edges] hover:overflow-y-auto',
                              modalSurface &&
                                '!min-h-[108px] bg-white/[0.07] px-4 py-3',
                              styles.input
                            )}
                            autoSize={{ minRows: 2 }}
                            variant="borderless"
                            disabled={genPromptLoading}
                            onChange={onImgPromptChange}
                            value={imgPrompt}
                            placeholder={t(
                              'Enter a text prompt or drop reference images.'
                            )}
                            style={{
                              resize: 'none',
                              background: 'rgb(0, 0, 0, 0)',
                              color: '#FFFFFF',
                            }}
                          />
                        </div>
                        {isImgPromptError && (
                          <div className="mt-[-16px] translate-y-[8px] text-xs text-[#FF5F5F]">
                            {t(
                              'Please keep your input within {{expr}} characters',
                              {
                                expr: MAX_IMAGE_PROMPT_LENGTH,
                              }
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="mb-2 ml-1 mt-3 text-sm text-white/60">
                          <div>{t('Image Style')}</div>
                        </div>
                        <div className="flex flex-col gap-3 rounded-lg bg-white/[0.04] p-2">
                          <div className="no-scrollbar flex gap-3 overflow-x-auto">
                            {Array.isArray(imgStyleData) &&
                              imgStyleData
                                .slice(0, Math.ceil(imgStyleData.length / 2))
                                .map((item: ImageStyle) => {
                                  const isSelected = imgStyleKey === item.id
                                  return (
                                    <div
                                      key={item.id}
                                      className="relative flex-shrink-0 overflow-hidden rounded-lg transition-all duration-200"
                                    >
                                      <button
                                        onClick={() => {
                                          if (imgStyleKey === item.id) {
                                            setImgStyleKey(null)
                                          } else {
                                            setImgStyleKey(item.id)
                                          }
                                        }}
                                        className="flex flex-col items-center justify-center overflow-hidden"
                                      >
                                        <img
                                          src={transformImageAuto(item.url)}
                                          loading="lazy"
                                          alt={item.name}
                                          className={clsx(
                                            'h-[110px] w-[110px] rounded-lg border-2 object-cover',
                                            modalSurface && 'h-[76px] w-[76px]',
                                            isSelected
                                              ? 'border-[#FF8A00]'
                                              : 'border-transparent'
                                          )}
                                        />
                                        <div
                                          className={clsx(
                                            'w-[110px] p-1',
                                            modalSurface && 'w-[76px]'
                                          )}
                                        >
                                          <div
                                            className={clsx(
                                              'text-gray-756A6E truncate text-xs font-light',
                                              {
                                                'text-[#FF8A00]': isSelected,
                                                'text-zinc-400': !isSelected,
                                              }
                                            )}
                                          >
                                            {item.name}
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                  )
                                })}
                          </div>
                          <div className="no-scrollbar flex gap-3 overflow-x-auto">
                            {Array.isArray(imgStyleData) &&
                              imgStyleData
                                .slice(Math.ceil(imgStyleData.length / 2))
                                .map((item: ImageStyle) => {
                                  const isSelected = imgStyleKey === item.id
                                  return (
                                    <div
                                      key={item.id}
                                      className="relative flex-shrink-0 overflow-hidden transition-all duration-200"
                                    >
                                      <button
                                        onClick={() => {
                                          if (imgStyleKey === item.id) {
                                            setImgStyleKey(null)
                                          } else {
                                            setImgStyleKey(item.id)
                                          }
                                        }}
                                        className="flex flex-col items-center justify-center overflow-hidden"
                                      >
                                        <img
                                          src={transformImageAuto(item.url)}
                                          loading="lazy"
                                          alt={item.name}
                                          className={clsx(
                                            'h-[110px] w-[110px] rounded-lg border-2 object-cover',
                                            modalSurface && 'h-[76px] w-[76px]',
                                            isSelected
                                              ? 'border-[#FF8A00]'
                                              : 'border-transparent'
                                          )}
                                        />
                                        <div
                                          className={clsx(
                                            'w-[110px] p-1',
                                            modalSurface && 'w-[76px]'
                                          )}
                                        >
                                          <div
                                            className={clsx(
                                              'text-gray-756A6E truncate text-xs font-light',
                                              {
                                                'text-[#FF8A00]': isSelected,
                                                'text-zinc-400': !isSelected,
                                              }
                                            )}
                                          >
                                            {item.name}
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                  )
                                })}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-white/60">
                        {t('Generate Quantity')}
                      </div>
                      <div className="mt-2 flex flex-col">
                        <div
                          className={clsx(
                            'flex flex-shrink-0 items-center justify-between gap-2',
                            modalSurface &&
                              'rounded-lg bg-white/[0.04] px-3 py-2'
                          )}
                        >
                          {/* <Select
                            className="!h-[30px] !w-[132px] !rounded-full !border !border-white/10 !bg-black/10 !px-3 !text-[#847379]"
                            options={GenerateNumList.map((item) => ({
                              label: item + 'pc',
                              value: item,
                            }))}
                            value={String(generatedNum)}
                            onChange={(value) => setGeneratedNum(Number(value))}
                            itemClassName=""
                            placement="topRight"
                            icon={<CaretDownOutlined className="text-white" />}
                          /> */}
                          <div className="text-sm text-[#847379]">4pc</div>
                          <div className="flex gap-1">
                            <div className="text-sm text-[#847379]">
                              {t('Consumption:')}
                            </div>
                            <div className="text-sm">
                              {20} {t('Gems')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {isPreviewStep && (
              <div className="flex h-full flex-col">
                <div className="relative pb-2 pt-5 text-[15px] tracking-[0.4px]">
                  {/* {t('My Tipsy')} */}
                  {` `}
                </div>
                <div className="flex flex-1 gap-3.5 rounded-lg bg-white/5 p-5">
                  <img
                    src={charImageUrl || ''}
                    alt={/* i18n-ignore */ 'Character'}
                    className="h-full max-h-[50vh] w-auto rounded-lg object-contain"
                  />
                  <div className="flex w-full flex-col gap-3.5">
                    <div className="flex flex-row">
                      {charFaceImagePreviewUrl && (
                        <Avatar
                          src={charFaceImagePreviewUrl}
                          alt=""
                          size={182}
                          shape="square"
                          className="!rounded-lg"
                        />
                      )}
                      {!charFaceImagePreviewUrl && charImageUrl && (
                        <AvatarEditorItemOutput
                          size={182}
                          image={charImageUrl}
                          croppedArea={charFaceImageCroppedArea}
                          loading={croppingCharFaceImage}
                          className="rounded-lg border-none"
                        />
                      )}
                      <div className="ml-[15px] flex flex-col items-center">
                        <div className="relative text-[15px] tracking-[0.4px]">
                          {t('Avatar')}
                        </div>
                        <TipsyButton
                          className="relative mx-auto mt-[9px] h-[30px] whitespace-nowrap px-4 text-xs leading-[30px]"
                          onClick={() => setEditFaceOpen(true)}
                          variant="filled"
                          loading={croppingCharFaceImage}
                        >
                          {t('Edit')}
                        </TipsyButton>
                      </div>
                    </div>
                  </div>
                </div>
                {charImageUrl && (
                  <AvatarEditorItem
                    image={charImageUrl}
                    open={editFaceOpen}
                    initialCroppedAreaPixels={charFaceImageCroppedAreaPixels}
                    title={t('Edit Avatar')}
                    onOk={handleSaveFaceImg}
                    onCancel={() => setEditFaceOpen(false)}
                  />
                )}
              </div>
            )}
            <div className="relative mt-[14px] w-full rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-[#999999]">
              <div className="inline-block translate-y-[3px]">
                <CreateRulesIcon />
              </div>
              {/* i18n-ignore */}
              &ensp;
              {t(
                'Character Creation Rules: Avoid nudity, characters under 18, or low-quality/unauthorized images without modification. Violations may lead to a public view ban'
              )}
            </div>
          </div>

          {/* button部分 */}
          {!isPreviewStep &&
            createImgStep === 'generateImg' &&
            !hasGeneratedImages && (
              <div
                className={clsx(
                  'absolute bottom-[20px] left-0 right-0 m-auto flex w-full justify-center gap-2.5',
                  modalSurface && 'bottom-[19px] px-6'
                )}
              >
                {/* {generatedLoading && (
                <TipsyButton
                  className="flex h-10 w-[153px] items-center justify-center"
                  onClick={handleStopGeneratingImage}
                >
                  <span className="h-3 w-3 rounded-sm bg-white" />
                </TipsyButton>
              )} */}
                {!generatedLoading && (
                  <TipsyButton
                    className={clsx(
                      'h-10 min-w-[153px] px-6',
                      modalSurface &&
                        'h-[42px] w-full max-w-[570px] rounded-[65px] border-transparent bg-[#9C4844] text-[16px] font-normal hover:border-transparent'
                    )}
                    variant="filled"
                    onClick={handleGenerateImg}
                    disabled={isImgPromptError || !imgPrompt}
                    color="#9C4844"
                  >
                    {t('Generate')}
                  </TipsyButton>
                )}
              </div>
            )}
          {!isPreviewStep &&
            hasGeneratedImages &&
            createImgStep === 'generateImg' && (
              <div
                className={clsx(
                  'absolute bottom-[20px] left-0 right-0 m-auto flex justify-center gap-2.5',
                  modalSurface && 'bottom-[19px] px-6'
                )}
              >
                {!generatedLoading && (
                  <TipsyButton
                    className={clsx(
                      'h-10 min-w-[153px] px-6',
                      modalSurface &&
                        'h-[42px] min-w-[180px] rounded-[65px] border-white/10 bg-white/5 text-[16px] font-normal hover:bg-white/10'
                    )}
                    onClick={handleGenerateImg}
                    disabled={generatedLoading || !imgPrompt}
                    loading={generatedLoading}
                  >
                    {t('Generate Again')}
                  </TipsyButton>
                )}
                {generatedLoading && (
                  <TipsyButton
                    className={clsx(
                      'flex h-10 min-w-[153px] items-center justify-center px-6',
                      modalSurface &&
                        'h-[42px] min-w-[180px] rounded-[65px] border-white/10 bg-white/5'
                    )}
                    onClick={handleStopGeneratingImage}
                  >
                    <span className="h-3 w-3 rounded-sm bg-white" />
                  </TipsyButton>
                )}
                <TipsyButton
                  className={clsx(
                    'h-10 min-w-[153px] px-6',
                    modalSurface &&
                      'h-[42px] min-w-[220px] rounded-[65px] border-transparent bg-[#9C4844] text-[16px] font-normal hover:border-transparent'
                  )}
                  variant="filled"
                  onClick={handleGoPreviewStep}
                  color="#9C4844"
                >
                  {t('Next')}
                </TipsyButton>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Upload' &&
            !uploadUploadImageUrl && (
              <div className="absolute bottom-[20px] left-0 right-0 m-auto flex w-full justify-center">
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={handleUploadImg}
                  loading={uploadImgUploading}
                  disabled={isImgPromptError}
                >
                  {t('Upload Image')}
                </TipsyButton>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Upload' &&
            uploadUploadImageUrl && (
              <div className="absolute bottom-[20px] left-0 right-0 m-auto flex justify-center gap-2.5">
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  onClick={handleUploadImg}
                  disabled={generatedLoading}
                >
                  {t('Upload Again')}
                </TipsyButton>
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={handleGoPreviewStep}
                  disabled={generatedLoading}
                >
                  {t('Next')}
                </TipsyButton>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Upscale' &&
            !uploadUpscaleImageUrl && (
              <div className="absolute bottom-[20px] left-0 right-0 m-auto flex w-full justify-center">
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={handleUploadImg}
                  loading={generatedLoading}
                  disabled={isImgPromptError}
                >
                  {t('Generate Image')}
                </TipsyButton>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Upscale' &&
            uploadUpscaleImageUrl && (
              <div className="absolute bottom-[20px] left-0 right-0 m-auto flex justify-center gap-2.5">
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  onClick={handleUploadImg}
                  disabled={generatedLoading}
                >
                  {t('Generate Again')}
                </TipsyButton>
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={handleGoPreviewStep}
                  disabled={generatedLoading}
                >
                  {t('Next')}
                </TipsyButton>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Vary' &&
            !uploadVaryImageUrl && (
              <div className="absolute bottom-[20px] left-0 right-0 m-auto flex w-full justify-center">
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={handleUploadImg}
                  loading={generatedLoading}
                  disabled={isImgPromptError}
                >
                  {t('Generate Image')}
                </TipsyButton>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Vary' &&
            uploadVaryImageUrl && (
              <div className="absolute bottom-[20px] left-0 right-0 m-auto flex justify-center gap-2.5">
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  onClick={handleUploadImg}
                  disabled={generatedLoading}
                >
                  {t('Generate Again')}
                </TipsyButton>
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={handleGoPreviewStep}
                  disabled={generatedLoading}
                >
                  {t('Next')}
                </TipsyButton>
              </div>
            )}

          {isPreviewStep && (
            <div className="absolute bottom-[20px] left-0 right-0 m-auto flex justify-center gap-2.5">
              <TipsyButton
                className="h-10 min-w-[153px] px-6"
                onClick={() => setIsPreviewStep(false)}
              >
                {isUpdateChar ? t('Edit Image') : t('Back')}
              </TipsyButton>
              {!hideNext && (
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={goNextStep}
                >
                  {t('Next')}
                </TipsyButton>
              )}
              {hideNext && onConfirm && (
                <TipsyButton
                  className="h-10 min-w-[153px] px-6"
                  variant="filled"
                  onClick={onConfirm}
                >
                  {t('Confirm')}
                </TipsyButton>
              )}
            </div>
          )}
        </div>

        <div
          className={clsx(
            'w-[30%] rounded-ee-[10px] rounded-se-[10px] border-[0.5px] border-white/20 px-[19px]',
            modalSurface &&
              'border-y-0 border-r-0 border-white/10 bg-white/[0.02]'
          )}
        >
          {!isPreviewStep &&
            createImgStep === 'generateImg' &&
            !hasGeneratedImages &&
            !generatedLoading && (
              <div className="flex h-full flex-col">
                <div className="mb-3 mt-5 text-center text-[15px] tracking-[0.4px] text-white">
                  {t('Preview')}
                </div>
                <div className="mx-auto mb-9 flex h-full w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                  <img
                    src="/create_icon_image.svg"
                    alt={/* i18n-ignore */ 'Default preview'}
                    className="inline-block"
                  />
                </div>
              </div>
            )}
          {!isPreviewStep &&
            ((createImgStep === 'uploadImg' &&
              uploadImgType === 'Upload' &&
              !uploadImgReferencePreviewUrl) ||
              (createImgStep === 'uploadImg' &&
                uploadImgType === 'Upscale' &&
                !uploadUpscaleImageUrl) ||
              (createImgStep === 'uploadImg' &&
                uploadImgType === 'Vary' &&
                !uploadVaryImageUrl)) &&
            !generatedLoading && (
              <div className="flex h-full flex-col">
                <div className="mb-3 mt-5 text-center text-[15px] tracking-[0.4px] text-white">
                  {t('Preview')}
                </div>
                <div className="mx-auto mb-9 flex h-full w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                  <img
                    src="/create_icon_image.svg"
                    alt={/* i18n-ignore */ 'Default preview'}
                    className="inline-block"
                  />
                </div>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            generatedLoading && (
              <div className="flex h-full flex-col pb-[34px]">
                <div className="mb-3 mt-5 flex items-center justify-center text-[15px] tracking-[0.4px] text-white">
                  <span>{t('Generated Results')}</span>
                </div>
                <div className="flex h-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                  <div>
                    <LoadingImg />
                    <div className="mt-1 text-center text-sm tracking-wide text-[#b3b3b3]">
                      <LoadingProgress isStart={true} time={30} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'generateImg' &&
            (generatedLoading || hasGeneratedImages) && (
              <div className="flex h-full flex-col pb-[18px]">
                <div className="mb-3 mt-5 flex items-center justify-center text-[15px] tracking-[0.4px] text-white">
                  <span>
                    {modalSurface ? t('Preview') : t('Generated Results')}
                  </span>
                </div>
                <div className="min-h-0 flex-1">
                  <div
                    className={clsx(
                      'm-auto h-full gap-2',
                      generatedNum === 1
                        ? 'flex'
                        : 'grid grid-cols-2 grid-rows-[max-content]',
                      modalSurface && 'gap-3'
                    )}
                    style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                  >
                    {generatedImages.map((item, index) => {
                      if (item) {
                        return (
                          <div
                            className="relative overflow-hidden rounded-xl"
                            style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                            key={index}
                            onClick={() => openImgGalleryWithIndex(index)}
                          >
                            <Image
                              unoptimized
                              src={item.image_url}
                              alt={/* i18n-ignore */ `Result ${index + 1}`}
                              className="object-cover"
                              fill={true}
                            />

                            {index === selectedImgIndex ? (
                              <div
                                className="absolute bottom-2.5 right-2.5 flex h-[24px] w-[24px] cursor-pointer items-center justify-center rounded-full border-2 border-white/50 bg-black/50"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleImgChecked(e, index)
                                }}
                              >
                                <CreateSelectIcon />
                              </div>
                            ) : (
                              <div
                                className={`absolute bottom-2.5 right-2.5 h-[24px] w-[24px] rounded-full border-2 border-white/50 bg-black/50 ${
                                  item.moderation_status === true
                                    ? 'cursor-not-allowed opacity-50'
                                    : 'cursor-pointer'
                                }`}
                                onClick={(e) => {
                                  if (item.moderation_status === true) {
                                    e.stopPropagation()
                                    messageApi.warning({
                                      content: t(
                                        'Cannot select failed moderation images'
                                      ),
                                      duration: 2,
                                    })
                                    return
                                  }
                                  handleImgChecked(e, index)
                                }}
                              ></div>
                            )}

                            {/* 审核未通过的遮罩层 */}
                            {item.moderation_status === true && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                                <div className="flex flex-col items-center text-center">
                                  <ExclamationCircleOutlined className="mb-1 text-xl text-red-500" />
                                  <span className="text-xs text-white">
                                    {t('Moderation Failed')}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      } else {
                        return (
                          <div
                            key={index}
                            className="relative flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]"
                            style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                          >
                            {generatedLoading && (
                              <div className="absolute flex flex-col items-center">
                                <LoadingImg />
                                {/* <div className="mt-1 text-center text-sm tracking-wide text-[#b3b3b3]">
                                <LoadingProgress
                                  isStart={
                                    index === 0 || !!generatedImages[index - 1]
                                  }
                                  time={index === 0 ? 30 : 15}
                                />
                              </div> */}
                              </div>
                            )}
                          </div>
                        )
                      }
                    })}
                  </div>
                </div>
                <div className="my-4 mt-3 text-center text-xs text-[#B3B3B3]">
                  {t('Click to enlarge image')}
                </div>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Upload' &&
            uploadImgReferencePreviewUrl && (
              <div className="flex h-full flex-col pb-[34px]">
                <div className="mb-2 mt-5 text-center text-[15px] tracking-[0.4px] text-[#B3B3B3]">
                  <span>{t('Preview')}</span>
                </div>
                <div className="min-h-0">
                  <div
                    className={`relative m-auto h-full overflow-hidden rounded-lg`}
                    style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                  >
                    {/* <Image
                    unoptimized
                    src={transformImage(
                      uploadUploadImageUrl || '',
                      undefined,
                      90
                    )}
                    alt="Preview"
                    className="object-cover"
                    fill={true}
                  /> */}
                    {croppedArea && (
                      <UploadCharImgOutput
                        url={uploadImgReferencePreviewUrl}
                        croppedArea={croppedArea}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Upscale' &&
            uploadUpscaleImageUrl && (
              <div className="flex h-full flex-col pb-[34px]">
                <div className="mb-2 mt-5 text-center text-[15px] tracking-[0.4px] text-[#B3B3B3]">
                  <span>{t('Generated Results')}</span>
                </div>
                <div className="min-h-0">
                  <div
                    className={`relative m-auto h-full overflow-hidden rounded-lg`}
                    style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                  >
                    <Image
                      unoptimized
                      src={transformImage(
                        uploadUpscaleImageUrl || '',
                        undefined,
                        90
                      )}
                      alt={/* i18n-ignore */ 'Preview'}
                      className="object-contain"
                      fill={true}
                    />
                  </div>
                </div>
              </div>
            )}
          {!isPreviewStep &&
            createImgStep === 'uploadImg' &&
            uploadImgType === 'Vary' &&
            uploadVaryImageUrl && (
              <div className="flex h-full flex-col pb-[34px]">
                <div className="mb-2 mt-5 text-center text-[15px] tracking-[0.4px] text-[#B3B3B3]">
                  <span>{t('Generated Results')}</span>
                </div>
                <div className="min-h-0">
                  <div
                    className={`relative m-auto h-full overflow-hidden rounded-lg`}
                    style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                  >
                    <Image
                      unoptimized
                      src={transformImage(
                        uploadVaryImageUrl || '',
                        undefined,
                        90
                      )}
                      alt={/* i18n-ignore */ 'Preview'}
                      className="object-contain"
                      fill={true}
                    />
                  </div>
                </div>
              </div>
            )}
          {isPreviewStep && (
            <div className="flex h-full flex-col pb-[34px]">
              <div className="mb-2 mt-5 text-center text-[15px] tracking-[0.4px] text-[#B3B3B3]">
                {t('Preview')}
              </div>
              <div className="min-h-0">
                <div
                  className={`relative m-auto h-full overflow-hidden rounded-lg`}
                  style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
                >
                  {charImageUrl && (
                    <Image
                      unoptimized
                      src={transformImage(charImageUrl || '', undefined, 90)}
                      alt={/* i18n-ignore */ 'Character'}
                      className="object-conrain"
                      fill={true}
                    />
                  )}
                  <div
                    className="absolute bottom-0 left-0 flex h-1/2 w-full flex-col justify-end px-[21px] pb-[21px]"
                    style={{
                      background:
                        'linear-gradient( 180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%)',
                    }}
                  >
                    <div className="absolute bottom-[21px] left-[21px] flex h-[78px] w-[78px] items-center justify-center rounded-[18px] border border-solid border-[rgba(255,255,255,0.8)]">
                      {charFaceImagePreviewUrl && (
                        <Avatar
                          src={charFaceImagePreviewUrl}
                          alt=""
                          size={64}
                          className="border border-white/50"
                        />
                      )}
                      {!charFaceImagePreviewUrl && charImageUrl && (
                        <AvatarEditorItemOutput
                          size={64}
                          className="border-white/50"
                          image={charImageUrl}
                          croppedArea={charFaceImageCroppedArea}
                          loading={croppingCharFaceImage}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <Modal
            open={openImgGallery}
            centered
            maskClosable
            closeIcon={false}
            footer={null}
            width="100%"
            onCancel={() => setOpenImgGallery(false)}
            styles={{
              content: {
                backgroundColor: 'transparent',
                padding: '38px 0 0 0',
              },
              mask: { backgroundColor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <div
              className="flex items-center justify-around"
              onClick={() => setOpenImgGallery(false)}
            >
              <CaretLeftOutlined
                className="inset-y-2/4 mr-2.5 h-[44px] w-[44px] cursor-pointer justify-self-center text-[44px]"
                style={{
                  color:
                    currImgGalleryIndex > 0
                      ? '#fff'
                      : 'rgba(255, 255, 255, 0.40)',
                }}
                onClick={handleClickLeft}
              />
              <div
                className="relative cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  checkCharImageByIndex(currImgGalleryIndex)
                }}
              >
                <img
                  src={generatedImages?.[currImgGalleryIndex]?.image_url}
                  alt={/* i18n-ignore */ `Result ${currImgGalleryIndex + 1}`}
                  style={{
                    objectFit: 'contain',
                    width: 'auto',
                    maxHeight: 'calc(100vh - 80px)',
                  }}
                />

                {currImgGalleryIndex === selectedImgIndex ? (
                  <div className="absolute bottom-[30px] right-[30px] flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full border-2 border-white/50 bg-black/50">
                    <CreateSelectLargeIcon />
                  </div>
                ) : (
                  <div className="absolute bottom-[30px] right-[30px] h-[36px] w-[36px] cursor-pointer rounded-full border-2 border-white/50 bg-black/50"></div>
                )}
              </div>

              <CaretRightOutlined
                className="inset-y-2/4 ml-2.5 h-[44px] w-[44px] cursor-pointer justify-self-center text-[44px]"
                style={{
                  color:
                    currImgGalleryIndex < generatedNum - 1
                      ? '#fff'
                      : 'rgba(255, 255, 255, 0.40)',
                }}
                onClick={handleClickRight}
              />
            </div>
            <div className="mb-1.5 mt-2 text-center text-base text-[#B3B3B3]">
              {currImgGalleryIndex + 1}/{generatedNum}
            </div>
          </Modal>
          <Modal
            open={openPcImgGallery}
            centered
            maskClosable
            closeIcon={false}
            footer={null}
            width="fit-content"
            onCancel={() => setOpenPcImgGallery(false)}
            styles={{
              content: { backgroundColor: 'transparent' },
              mask: { backgroundColor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <div>
              <CaretLeftOutlined
                className="absolute inset-y-2/4 left-[-60px] cursor-pointer text-[44px]"
                style={{ color: '#B3B3B3' }}
                onClick={handlePcImgGalleryClickLeft}
              />
              <div className="relative w-[70vw]">
                <img
                  src={generatedPcImages?.[currPcImgGalleryIndex]?.url}
                  alt={/* i18n-ignore */ `Result ${currImgGalleryIndex + 1}`}
                />
                {currPcImgGalleryIndex === selectedPcImgIndex ? (
                  <div className="absolute bottom-[30px] right-[30px] flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full border-2 border-white/50 bg-black/50">
                    <CreateSelectLargeIcon />
                  </div>
                ) : (
                  <div
                    className="absolute bottom-[30px] right-[30px] h-[36px] w-[36px] cursor-pointer rounded-full border-2 border-white/50 bg-black/50"
                    onClick={() =>
                      checkCharPcImageByIndex(currPcImgGalleryIndex)
                    }
                  ></div>
                )}
              </div>
              <CaretRightOutlined
                className="absolute inset-y-2/4 right-[-60px] ml-[60px] cursor-pointer text-[44px]"
                style={{ color: '#B3B3B3' }}
                onClick={handlePcImgGalleryClickRight}
              />
            </div>
            <div className="mb-2 mt-2 text-center text-base text-[#B3B3B3]">
              {currPcImgGalleryIndex + 1}/2
            </div>
          </Modal>
        </div>
      </div>
    </>
  )
}
